import { useKeyPress } from 'ahooks'
import cn from 'classnames'
import { motion, useDragControls } from 'framer-motion'
import React, { useId, useRef } from 'react'
import ReactDOM from 'react-dom'

import { IconButtonCourse } from 'components/Editor/EditorElements/courseKit/ButtonCourse'
import CollapseCourse from 'components/Editor/EditorElements/courseKit/CollapseCourse'

import * as s from './DraggablePanel.module.scss'

const ANIMATIONS = {
  open: {
    opacity: 1,
    scale: 1,
  },
  closed: {
    opacity: 0,
    scale: 0.95,
  },
}

interface IPanelProps {
  children: React.ReactNode
  header?: React.ReactNode
  onClose: (e: React.MouseEvent | KeyboardEvent) => void
  refRoot?: React.MutableRefObject<HTMLDivElement | null>
  shakeKey?: number
  ariaLabelledBy?: string
  ariaDescribedBy?: string
}

const Panel: React.FC<IPanelProps> = ({
  refRoot,
  children,
  onClose,
  header,
  shakeKey,
  ariaLabelledBy,
  ariaDescribedBy,
}) => {
  const ref = useRef(null)
  const contentId = useId()
  const wasDraggingRef = useRef(false)
  const prevShakeKeyRef = useRef<number | undefined>(undefined)
  const shouldShake = useRef(false)

  if (prevShakeKeyRef.current !== shakeKey) {
    shouldShake.current = prevShakeKeyRef.current !== undefined
    prevShakeKeyRef.current = shakeKey
  }

  const dragControls = useDragControls()

  useKeyPress(27, onClose, { target: refRoot })

  const handlePointerDown = (e: React.PointerEvent) => {
    wasDraggingRef.current = false
    dragControls.start(e)
  }

  const handleDragStart = () => {
    wasDraggingRef.current = true
  }

  const handleHeaderClickCapture = (e: React.MouseEvent) => {
    if (wasDraggingRef.current) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  let styles = {}

  const asideElement = document.querySelector('[data-editor-panel="Toolbar"]') as HTMLElement
  if (asideElement) {
    const rect = asideElement.getBoundingClientRect()
    styles = {
      top: rect?.top ?? 100,
      right: (rect?.width ?? 100) + 12,
    }
  }

  return ReactDOM.createPortal(
    <motion.div
      animate='open'
      className={s.wrapper}
      exit='closed'
      initial='closed'
      ref={ref}
      transition={{ duration: 0.2 }}
      variants={ANIMATIONS}
    >
      <motion.div
        animate={shouldShake.current ? { x: [0, -6, 6, -6, 6, 0] } : {}}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        className={s.popover}
        dragConstraints={ref}
        dragControls={dragControls}
        dragElastic={0}
        dragListener={false}
        dragMomentum={false}
        initial={{ x: 0 }}
        key={shakeKey}
        onDragStart={handleDragStart}
        ref={refRoot}
        role='dialog'
        style={styles}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        drag
      >
        <div
          onPointerDown={(e) => {
            dragControls.start(e)
            handlePointerDown(e)
          }}
        >
          <CollapseCourse
            header={({ open }) => (
              <div
                className={cn(s.header, { [s.collapsed]: !open })}
                onClickCapture={handleHeaderClickCapture}
              >
                {header}
                <motion.div
                  className={s.icons}
                  drag={false}
                  onClickCapture={(e) => e.preventDefault()}
                >
                  <IconButtonCourse
                    aria-controls={contentId}
                    aria-expanded={open}
                    aria-label='Collapse'
                    icon={open ? 'iconsForElementsDown' : 'iconsForElementsLeft'}
                    name='drawerToggle'
                    styleType='ghost'
                  />
                  <IconButtonCourse
                    aria-label='Close'
                    icon='otherClose'
                    name='drawerClose'
                    onClick={onClose}
                    styleType='ghost'
                  />
                </motion.div>
              </div>
            )}
            name='incompleteElement'
            styles={{ maxHeight: 506 }}
            styleType='ghost'
            initialOpen
          >
            <motion.div className={s.content} id={contentId} role='region' layout layoutRoot>
              {children}
            </motion.div>
          </CollapseCourse>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  )
}

export default React.memo(Panel) as typeof Panel
